import React, { useState } from "react"
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  ErrorOutline,
  SaveOutlined,
  StorefrontOutlined
} from "@mui/icons-material"
import { Product } from "@/shared/types/product"
import { isEmpty } from "lodash"
import DownloadImagesButton from "@/features/listings/components/DownloadImagesButton"
import ImageSelectionList from "@/features/listings/components/ImageSelectionList"
import { useImageSelection } from "@/features/listings/hooks/useImageSelection"
import { ImageGalleryItem } from "@/shared/components/image-gallery"
import { PRODUCT_CATEGORIES } from "@/shared/constants/categories"
import { AMAZON_PUBLISHING_ENABLED } from "@/shared/constants/featureFlags"

type TabValue = 0 | 1
const MAX_KEYWORDS = 10
const MAX_KEYWORD_LENGTH = 60
const KEYWORDS_INPUT_MAX_LENGTH = 620

export interface EditableProductFields {
  name: string
  description: string
  keywords: string[]
  category: string
  price: string
}

interface Props {
  product: Product
  onDelete: (product: Product) => void
  onView: (items: ImageGalleryItem[], initialIndex: number) => void
  isSelected: boolean
  onToggleSelect: (id: string) => void
  onRegenerateSame: (product: Product, imageIndex: number) => void
  onRegenerateDifferent: (product: Product, imageIndex: number) => void
  isRegenerating: (productId: string, imageIndex: number) => boolean
  regenerateDisabledReason?: string | null
  onUpdate: (productId: string, updates: EditableProductFields) => Promise<void>
  onPushToAmazon: (product: Product) => Promise<void>
  amazonPublishingAvailable: boolean
  canPublishToAmazon: boolean
}

const categoryLabel = (cat: string) => (cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : "—")
const parseKeywords = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(",")
        .map(keyword => keyword.trim().slice(0, MAX_KEYWORD_LENGTH))
        .filter(Boolean)
        .slice(0, MAX_KEYWORDS)
    )
  )

const getDraftFromProduct = (product: Product): EditableProductFields => ({
  name: product.name ?? "",
  description: product.description ?? "",
  keywords: product.keywords ?? [],
  category: product.category ?? "",
  price: product.price ?? ""
})

const ProductListingCard: React.FC<Props> = ({
  onView,
  product,
  onDelete,
  isSelected,
  onToggleSelect,
  onUpdate,
  onPushToAmazon,
  amazonPublishingAvailable,
  canPublishToAmazon
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const generatedImageUrls = product.generatedImageUrls
  const hasGenerated = generatedImageUrls.length > 0
  const hasOriginal = product.imageUrls?.length > 0
  const [activeTab, setActiveTab] = useState<TabValue>(0)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pushing, setPushing] = useState(false)
  const [draft, setDraft] = useState<EditableProductFields>(() => getDraftFromProduct(product))
  const [keywordsInput, setKeywordsInput] = useState(() => (product.keywords ?? []).join(", "))

  const handleTabChange = (_e: React.SyntheticEvent, val: TabValue) => {
    setActiveTab(val)
    setSelectedIndex(0)
  }

  const handleEdit = () => {
    setDraft(getDraftFromProduct(product))
    setKeywordsInput((product.keywords ?? []).join(", "))
    setIsEditing(true)
  }

  const handleCancel = () => {
    setDraft(getDraftFromProduct(product))
    setKeywordsInput((product.keywords ?? []).join(", "))
    setIsEditing(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onUpdate(product.id, draft)
      setIsEditing(false)
    } finally {
      setSaving(false)
    }
  }

  const handlePushToAmazon = async () => {
    setPushing(true)
    try {
      await onPushToAmazon(product)
    } finally {
      setPushing(false)
    }
  }

  const activeUrls = (activeTab === 0 ? product.generatedImageUrls : product.imageUrls) ?? []
  const imageSelection = useImageSelection(activeUrls)

  const viewItems: ImageGalleryItem[] =
    activeTab === 0
      ? generatedImageUrls.map((url, index) => ({
          id: `${product.id}-${index}`,
          url,
          alt: `${product.name} generated image ${index + 1}`,
          metadata: {
            productId: product.id,
            generatedImageIndex: index,
            promptId: product.selectedPromptIds?.[index] ?? null
          }
        }))
      : (product.imageUrls ?? []).map((url, index) => ({
          id: `${product.id}-original-${index}`,
          url,
          alt: `${product.name} original image ${index + 1}`
        }))

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        ...(isSelected && {
          borderColor: "primary.main",
          bgcolor: "action.selected"
        })
      }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} mb={1} flexWrap="wrap">
          <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
            <Checkbox size="small" checked={isSelected} onChange={() => onToggleSelect(product.id)} sx={{ p: 0.5 }} />
            {isEditing ? (
              <TextField
                fullWidth
                size="small"
                label="Product Name"
                value={draft.name}
                onChange={event => setDraft(prev => ({ ...prev, name: event.target.value }))}
                inputProps={{ maxLength: 200 }}
                sx={{ minWidth: 0 }}
              />
            ) : (
              <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ minWidth: 0 }}>
                {product.name || "Untitled Product"}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={0.5} alignItems="center" flexShrink={0} flexWrap="wrap">
            {isEditing ? (
              <>
                <Tooltip title={saving ? "Updating…" : "Save changes"}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => void handleSave()}
                      disabled={saving}
                      aria-label="Update">
                      {saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel">
                  <span>
                    <IconButton size="small" onClick={handleCancel} disabled={saving} aria-label="Cancel">
                      <CloseOutlined />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <Tooltip title="Edit details">
                <IconButton size="small" color="primary" onClick={handleEdit} aria-label="Edit">
                  <EditOutlined />
                </IconButton>
              </Tooltip>
            )}
            {!isEditing && (
              <>
                <Tooltip title="Delete product">
                  <IconButton size="small" color="error" onClick={() => onDelete(product)} aria-label="Delete">
                    <DeleteOutlined />
                  </IconButton>
                </Tooltip>
                <DownloadImagesButton
                  imageUrls={product.generatedImageUrls}
                  selectedImageUrls={imageSelection.getSelectedUrls(product.generatedImageUrls)}
                  productName={product.name}
                />
                {AMAZON_PUBLISHING_ENABLED && amazonPublishingAvailable && (
                  <Tooltip title={canPublishToAmazon ? "Publish to Amazon" : "Connect your Amazon store first"}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => void handlePushToAmazon()}
                        disabled={pushing || !canPublishToAmazon}
                        aria-label="Publish to Amazon">
                        {pushing ? <CircularProgress size={18} color="inherit" /> : <StorefrontOutlined />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
            {product.quotaExceeded && (
              <Chip
                icon={<ErrorOutline fontSize="small" />}
                label="Quota Exceeded"
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Stack>
        </Stack>

        {(hasGenerated || hasOriginal) && (
          <>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="Generated Images" value={0} />
              <Tab label="Original Images" value={1} />
            </Tabs>

            {activeUrls.length > 0 ? (
              <>
                <Box
                  component="img"
                  src={activeUrls[selectedIndex] ?? activeUrls[0]}
                  onClick={() => {
                    onView(viewItems, selectedIndex)
                  }}
                  alt={`Preview of ${product.name}`}
                  sx={{
                    width: "100%",
                    height: { xs: 250, sm: 300, md: 350 },
                    objectFit: "contain",
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    display: "block",
                    ":hover": { cursor: "zoom-in" }
                  }}
                />
              </>
            ) : (
              <Stack alignItems="center" gap={1} py={4}>
                <CircularProgress sx={{ display: "block", mx: "auto", my: 4 }} />
                <Typography variant="body2" color="text.secondary">
                  Generating images...
                </Typography>
              </Stack>
            )}
            {activeUrls.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <ImageSelectionList
                  urls={activeUrls}
                  previewIndex={selectedIndex}
                  onPreviewChange={setSelectedIndex}
                  selectedIndices={imageSelection.selectedIndices}
                  onToggle={imageSelection.toggleImageSelection}
                />
              </>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />
        {!isEditing &&
          isEmpty(product.category) &&
          isEmpty(product.price) &&
          isEmpty(product.description) &&
          isEmpty(product.keywords) && (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No additional product details provided.
            </Typography>
          )}
        {isEditing ? (
          <Stack gap={2}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={draft.description}
              onChange={event =>
                setDraft(prev => ({
                  ...prev,
                  description: event.target.value
                }))
              }
              multiline
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Keywords"
              value={keywordsInput}
              onChange={event => {
                setKeywordsInput(event.target.value)
                setDraft(prev => ({
                  ...prev,
                  keywords: parseKeywords(event.target.value)
                }))
              }}
              inputProps={{ maxLength: KEYWORDS_INPUT_MAX_LENGTH }}
              helperText="Comma-separated SEO tags (up to 10 keywords)."
            />
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <FormControl size="small" fullWidth>
                <InputLabel id={`category-label-${product.id}`}>Category</InputLabel>
                <Select
                  id={`category-${product.id}`}
                  labelId={`category-label-${product.id}`}
                  label="Category"
                  value={draft.category}
                  onChange={event =>
                    setDraft(prev => ({
                      ...prev,
                      category: event.target.value
                    }))
                  }>
                  {PRODUCT_CATEGORIES.map(category => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                fullWidth
                label="Price"
                type="number"
                value={draft.price}
                onChange={event => setDraft(prev => ({ ...prev, price: event.target.value }))}
                inputProps={{ min: 0, inputMode: "decimal" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Stack>
          </Stack>
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2} flexWrap="wrap">
              {product.category && (
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {categoryLabel(product.category)}
                  </Typography>
                </Stack>
              )}
              {product.price && (
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    Price
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    ₹{product.price}
                  </Typography>
                </Stack>
              )}
              {product.description && (
                <Stack flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body2">{product.description}</Typography>
                </Stack>
              )}
            </Stack>
            {product.keywords && product.keywords.length > 0 && (
              <Stack gap={0.75} sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  SEO Keywords
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                  {product.keywords.map(keyword => (
                    <Chip key={keyword} label={keyword} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default ProductListingCard
